import React, { useState } from 'react';
import client from 'utils/client';

const FORMATS = [
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
  { value: 'pdf', label: 'PDF' },
];

const PERIODS = [
  { value: '', label: 'Custom range' },
  { value: 'daily', label: 'Today' },
  { value: 'weekly', label: 'This week' },
  { value: 'monthly', label: 'This month' },
  { value: '3months', label: 'Last 3 months' },
  { value: '6months', label: 'Last 6 months' },
  { value: '1year', label: 'This year' },
];

// Trigger a file download from an axios blob response.
const MAX_TOP_N = 100;

const saveBlob = (response, fallbackName) => {
  const disposition = response.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const filename = match ? match[1] : fallbackName;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Axios errors from `responseType: 'blob'` have a Blob body; read it as text to recover the JSON message.
const extractErrorMessage = async (err) => {
  const data = err?.response?.data || err?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try { return JSON.parse(text).message || text; } catch { return text; }
    } catch { /* fall through */ }
  }
  if (typeof data === 'string') return data;
  return data?.message || err?.message || 'Export failed';
};

function ReportDialog({ onClose, defaultMode = 'report', filters: tableFilters = {} }) {
  const [format, setFormat] = useState('xlsx');
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [topN, setTopN] = useState(10);
  const [mode, setMode] = useState(defaultMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    // Custom range bypasses the period whitelist on the backend, so an unbounded
    // export would scan the whole table. Require both ends of the range here.
    if (!period && (!startDate || !endDate)) {
      setError('Custom range requires both a start date and an end date.');
      return;
    }
    if (!period && startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setError('Start date must be on or before end date.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const filters = { ...tableFilters, format, groupBy, topN };
      if (period) filters.period = period;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const endpoint = mode === 'report' ? '/transactions/report' : '/transactions/download';

      if (format === 'json') {
        const res = await client.post(endpoint, filters);
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${mode}_${Date.now()}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const res = await client.post(endpoint, filters, { responseType: 'blob' });
        saveBlob(res, `${mode}_${Date.now()}.${format}`);
      }
      onClose?.();
    } catch (err) {
      setError(await extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onTopNChange = (e) => {
    const raw = parseInt(e.target.value, 10);
    if (Number.isNaN(raw)) return setTopN(10);
    setTopN(Math.min(Math.max(raw, 1), MAX_TOP_N));
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <h2 style={{ marginTop: 0 }}>Generate Report</h2>

        <label style={label}>Type</label>
        <select value={mode} onChange={(e) => setMode(e.target.value)} style={input}>
          <option value="report">Full report (summary + trend + transactions)</option>
          <option value="export">Transactions only</option>
        </select>

        <label style={label}>Format</label>
        <select value={format} onChange={(e) => setFormat(e.target.value)} style={input}>
          {FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>

        <label style={label}>Period</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} style={input}>
          {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>

        {!period && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={label}>Start date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={input} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={label}>End date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={input} />
            </div>
          </div>
        )}

        {mode === 'report' && (
          <>
            <label style={label}>Trend grouping</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={input}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>

            <label style={label}>Top N (merchants/terminals, max {MAX_TOP_N})</label>
            <input type="number" min={1} max={MAX_TOP_N} value={topN} onChange={onTopNChange} style={input} />
          </>
        )}

        {error && <div style={{ color: 'crimson', marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-tertiary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200,
};
const modal = {
  background: 'white', padding: 24, borderRadius: 8, minWidth: 420, maxWidth: 520,
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};
const label = { display: 'block', marginTop: 12, marginBottom: 4, fontSize: 13, color: '#444' };
const input = {
  width: '100%', padding: '8px 10px', border: '1px solid #ccc',
  borderRadius: 4, fontSize: 14, boxSizing: 'border-box',
};
export default ReportDialog;
